import { SkeletonAvatar } from "../../utils/SkeletonLoader";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GLOBAL_IMAGES } from "../../assets/asset";
import useProfileAvatars from "../../features/queryHooks/useProfileAvatars";
import { useUpdateUser } from "../../features/auth/useLogin";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setIsNewUser } from "../../features/auth/user.reducer";
import {
  getAfterLoginInitialStep,
  getProfileCompletionState,
} from "../../features/auth/profileCompletion";
import {
  clearParentPostAuthRedirect,
  isParentSessionMode,
  readParentPostAuthRedirect,
} from "../../features/parentFlow/parentFlow";

const AfterLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loginData } = location.state || {};
  const redirectAfterComplete =
    location.state?.redirectAfterComplete || readParentPostAuthRedirect("");
  const isParentMode = isParentSessionMode();
  const { user } = useSelector((state) => state.user);
  const onboardingProfile = loginData || user;
  const {
    normalizedUserName,
    normalizedDob,
    normalizedAvatar,
    isNameMissing,
    isDobMissing,
  } = getProfileCompletionState(onboardingProfile);
  const profileAvatarQuery = useProfileAvatars();
  const updateUserMutation = useUpdateUser();
  const [currentStep, setCurrentStep] = useState(() =>
    getAfterLoginInitialStep(onboardingProfile)
  );
  const [formData, setFormData] = useState(() => ({
    username: normalizedUserName,
    dob: normalizedDob,
    profilephoto: normalizedAvatar,
  }));
  const [formErrors, setFormErrors] = useState({
    dob: "",
  });

  const calculateDateLimits = () => {
    const today = new Date();
    const maxDate = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate()
    );

    const minDate = new Date(
      today.getFullYear() - 60,
      today.getMonth(),
      today.getDate()
    );

    return {
      max: maxDate.toISOString().split("T")[0],
      min: minDate.toISOString().split("T")[0],
    };
  };

  const dateLimits = calculateDateLimits();

  useEffect(() => {
    setCurrentStep(isNameMissing ? 1 : isDobMissing ? 2 : 3);
    setFormData({
      username: normalizedUserName,
      dob: normalizedDob,
      profilephoto: normalizedAvatar,
    });
  }, [
    isNameMissing,
    isDobMissing,
    normalizedUserName,
    normalizedDob,
    normalizedAvatar,
  ]);

  const newUpdateSignup = async () => {
    if (!isValidDob(formData.dob)) {
      setCurrentStep(2);
      setFormErrors((prev) => ({
        ...prev,
        dob: "Please enter a valid date of birth",
      }));
      return;
    }

    updateUserMutation.mutate(
      {
        id: user?.UserId || user?.pk,
        user_name: formData.username,
        parents_date_of_birth: formData.dob,
        profilephotos: formData.profilephoto,
      },
      {
        onSuccess: () => {
          if (redirectAfterComplete) {
            clearParentPostAuthRedirect();
            navigate(redirectAfterComplete, { replace: true });
            return;
          }

          navigate("/yearsdashboard");
          dispatch(setIsNewUser(true));
        },
        onError: (error) => {
          const serverMessage = error?.response?.data?.message || "";
          if (
            serverMessage === "Request data are incorrect" ||
            serverMessage.toLowerCase().includes("date")
          ) {
            setCurrentStep(2);
            setFormErrors((prev) => ({
              ...prev,
              dob: "Please enter a valid date of birth",
            }));
            return;
          }
          toast.error("Failed to update user data");
        },
      }
    );
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === "dob") {
      setFormErrors((prev) => ({ ...prev, dob: "" }));
    }
  };

  const handleNext = async () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleAvatarSelect = async (avatarUrl) => {
    setFormData({ ...formData, profilephoto: avatarUrl });
  };

  const isValidDob = (dob) => {
    if (!dob) return false;
    if (dob < dateLimits.min || dob > dateLimits.max) return false;
    return true;
  };

  const handleDobContinue = () => {
    if (!isValidDob(formData.dob)) {
      setFormErrors((prev) => ({
        ...prev,
        dob: "Please enter a valid date of birth",
      }));
      return;
    }

    setFormErrors((prev) => ({ ...prev, dob: "" }));
    handleNext();
  };

  const buttonStyles = isParentMode
    ? "rounded-full bg-[#08059B] px-[50px] py-[10px] text-sm font-bold text-white shadow-[0_8px_18px_rgba(8,5,155,0.2)] transition hover:bg-[#01044F]"
    : "text-white bg-[#08059B] px-[50px] py-[7px] rounded-full";
  const backButtonStyles = isParentMode
    ? "mr-4 rounded-full border border-[#d8cfbf] bg-white px-[50px] py-[10px] text-sm font-bold text-[#0B2B66] transition hover:border-[#08059B] hover:text-[#08059B]"
    : "text-[#08059B] border border-[#08059B] px-[50px] py-[7px] rounded-full mr-4";
  const containerClassName = isParentMode
    ? "relative min-h-screen overflow-hidden bg-[#f8f3ea] font-mali text-[#1b1b1b]"
    : "min-h-screen flex flex-col items-center justify-center bg-blue-50 font-mali";
  const wrapperClassName = isParentMode
    ? "relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col items-center justify-center px-4 py-10"
    : "min-h-screen flex flex-col items-center justify-center";
  const cardClassName = isParentMode
    ? "w-full max-w-xl rounded-[32px] border border-[#e5dacb] bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(255,247,239,0.94)_100%)] p-8 shadow-[0_24px_60px_rgba(141,107,70,0.16)] backdrop-blur-sm sm:p-10"
    : "bg-white bg-opacity-90 p-8 rounded-[20px] shadow-lg max-w-md w-full";
  const headingClassName = isParentMode
    ? "mb-3 text-[30px] font-bold leading-tight text-[#0B2B66]"
    : "text-2xl font-semibold mb-6";
  const subheadingClassName = isParentMode
    ? "mb-6 text-sm font-medium leading-6 text-[#5f6576]"
    : "sr-only";
  const inputClassName = isParentMode
    ? "w-full rounded-[20px] border border-[#ddd3c6] bg-[#fffaf5] px-5 py-4 text-base font-medium text-[#1f2740] outline-none transition placeholder:text-[#8a8f9f] focus:border-[#08059B] focus:ring-2 focus:ring-[#08059B]/10"
    : "w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";
  const avatarGridClassName = isParentMode
    ? "grid grid-cols-4 gap-4 sm:grid-cols-5"
    : "grid grid-cols-5 gap-4";

  return (
    <div
      className={containerClassName}
      style={
        isParentMode
          ? undefined
          : {
              backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
      }
    >
      {!isParentMode && (
        <div className="absolute top-6 left-6">
          <img
            src={GLOBAL_IMAGES.dingo}
            alt="Mathdingo Logo"
            className="w-20 h-20"
          />
        </div>
      )}

      {isParentMode && (
        <>
          <div className="absolute inset-x-0 top-0 h-[320px] bg-[radial-gradient(circle_at_top_left,rgba(255,215,180,0.65),transparent_50%),radial-gradient(circle_at_top_right,rgba(255,239,191,0.85),transparent_42%),linear-gradient(180deg,#fdf7f0_0%,#f8f3ea_65%,#f6efe6_100%)]" />
          <div className="absolute -left-16 top-28 h-48 w-48 rounded-full bg-[#f8caa4]/40 blur-3xl" />
          <div className="absolute -right-10 bottom-16 h-56 w-56 rounded-full bg-[#fde8a8]/55 blur-3xl" />
        </>
      )}

      <div className={wrapperClassName}>
        <img
          className={
            isParentMode
              ? "mb-8 h-28 w-auto object-contain sm:h-32"
              : "mb-8 w-[58%] max-w-[420px]"
          }
          src={GLOBAL_IMAGES.dingoLogoText}
          alt="Dingo Text Logo"
        />

        {isParentMode && (
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#e5dacb] bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#9d5229]">
            Parent setup
          </div>
        )}

      <div className={cardClassName}>
        {currentStep === 1 && (
          <div className="space-y-4">
            <h2 className={headingClassName}>
              Enter your username:
            </h2>
            {isParentMode && (
              <p className={subheadingClassName}>
                Add the student name you want to use in the parent booking flow.
              </p>
            )}
            <input
              name="username"
              placeholder="Enter username"
              value={formData.username}
              onChange={handleChange}
              className={inputClassName}
            />
            <div className="flex justify-end mt-6">
              <button
                onClick={handleNext}
                disabled={!formData.username.trim()}
                className={`${buttonStyles} ${
                  !formData.username.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <>
            <div className="space-y-4">
              <h2 className={headingClassName}>
                Enter your date of birth:
              </h2>
              {isParentMode && (
                <p className={subheadingClassName}>
                  This helps us complete the linked student account details.
                </p>
              )}
              <input
                name="dob"
                type="date"
                value={formData.dob}
                onChange={handleChange}
                min={dateLimits.min}
                max={dateLimits.max}
                className={inputClassName}
              />
              {formErrors.dob && (
                <p className="text-red-600 text-sm mt-1">{formErrors.dob}</p>
              )}
              <div className="flex justify-center mt-6">
                <button onClick={handleBack} className={backButtonStyles}>
                  Back
                </button>
                <button
                  onClick={handleDobContinue}
                  className={buttonStyles}
                >
                  Continue
                </button>
              </div>
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            {profileAvatarQuery.isPending ? (
              <SkeletonAvatar />
            ) : (
              <div className="space-y-4">
                <h2 className={headingClassName}>
                  Choose an Avatar:
                </h2>
                {isParentMode && (
                  <p className={subheadingClassName}>
                    Pick the avatar that should appear in the parent booking area.
                  </p>
                )}
                <div className={avatarGridClassName}>
                  {profileAvatarQuery?.data?.data.map((item, i) => (
                    <img
                      key={i}
                      src={item.fields.avatarUrl}
                      alt={`Avatar ${i}`}
                      className={`w-full aspect-square rounded-full cursor-pointer transition-all ${
                        formData.profilephoto === item.fields.avatarUrl
                          ? isParentMode
                            ? "ring-4 ring-[#f4b467] scale-110 shadow-[0_10px_24px_rgba(244,180,103,0.35)]"
                            : "ring-4 ring-blue-500 scale-110"
                          : isParentMode
                            ? "border border-[#f0e2d2] bg-white/80 p-1 hover:scale-105"
                            : "hover:scale-105"
                      }`}
                      onClick={() => handleAvatarSelect(item.fields.avatarUrl)}
                    />
                  ))}
                </div>
                <div className="flex justify-center mt-6">
                  <button onClick={handleBack} className={backButtonStyles}>
                    Back
                  </button>
                  <button
                    onClick={newUpdateSignup}
                    disabled={!formData.profilephoto}
                    className={`${buttonStyles} ${
                      !formData.profilephoto
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Get Started
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default AfterLogin;
